<template>
  <FormContainer>
    <template #title>Coordonnées de l'apprenti(e)</template>
    <template #content>
      <div class="form__bloc">
        <WrapperInput
          id="field_47"
          v-model="voieModel"
          :label="LABEL_NUMERO_VOIE"
          class="col-12 col-md-18 col-sm-36"
          :placeholder="PLACEHOLDER_ADR_VOIE"
          :required="meta.apprenti?.adresse?.voie?.$status === PropertyStatus.Required"
          :alertLabel="meta.apprenti?.adresse?.voie?.$messageErreurDirty!"
          :maxlength="38"
          deletable
          :readonly
        />
        <WrapperInput
          id="field_47"
          v-model="complementAdresseModel"
          label="Complément d'adresse"
          class="col-12 col-md-18 col-sm-36"
          :placeholder="PLACEHOLDER_ADR_COMPLEMENT"
          :required="meta.apprenti?.adresse?.complement?.$status === PropertyStatus.Required"
          :alertLabel="meta.apprenti?.adresse?.complement?.$messageErreurDirty!"
          :maxlength="38"
          deletable
          :readonly
        />
        <WrapperAutocomplete
          id="field_48"
          key-id="codeInsee"
          key-label="intitule"
          :search="searchCommune"
          v-model="codePostalModel"
          :get-item-label="getCpCommuneLabel"
          label="Code postal"
          :search-min-length="3"
          :placeholder="PLACEHOLDER_ADR_CP"
          :readonly
          deletable
          class="col-4 col-md-6 col-sm-36"
          :strict="false"
          :required="meta.apprenti?.adresse?.codePostal?.$status === PropertyStatus.Required"
          :alertLabel="meta.apprenti?.adresse?.codePostal?.$messageErreurDirty!"
          :maxlength="5"
        />
        <WrapperAutocomplete
          id="field_49"
          key-id="codeInsee"
          key-label="intitule"
          :search="searchCommune"
          v-model="communeModel"
          :get-item-label="getCpCommuneLabel"
          label="Commune"
          :search-min-length="3"
          :placeholder="PLACEHOLDER_ADR_VILLE"
          :readonly
          deletable
          class="col-8 col-md-12 col-sm-36"
          :strict="false"
          :required="meta.apprenti?.adresse?.commune?.$status === PropertyStatus.Required"
          :alertLabel="meta.apprenti?.adresse?.commune?.$messageErreurDirty!"
          :maxlength="50"
        />
      </div>
      <div class="form__bloc">
        <WrapperInputTelephone
          id="field_50"
          v-model="telephoneModel"
          label="Téléphone"
          :required="meta.apprenti?.telephone?.$status === PropertyStatus.Required"
          :alertLabel="meta.apprenti?.telephone?.$messageErreurDirty!"
          class="col-6 col-md-12 col-sm-36"
          :placeholder="PLACEHOLDER_TEL"
          :maxlength="10"
          deletable
          :readonly
        />
        <WrapperInput
          id="field_51"
          v-model="emailModel"
          label="Email"
          :required="meta.apprenti?.email?.$status === PropertyStatus.Required"
          :alertLabel="meta.apprenti?.email?.$messageErreurDirty!"
          hint="64 caractères maximum"
          class="col-10 col-md-18 col-sm-36"
          :placeholder="PLACEHOLDER_EMAIL"
          :maxlength="64"
          deletable
          :readonly
        />
      </div>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import { storeToRefs } from 'pinia'
  import {
    FormContainer,
    PLACEHOLDER_ADR_VOIE,
    PLACEHOLDER_ADR_COMPLEMENT,
    PLACEHOLDER_ADR_CP,
    PLACEHOLDER_ADR_VILLE,
    PLACEHOLDER_TEL,
    PLACEHOLDER_EMAIL,
    LABEL_NUMERO_VOIE,
  } from '@/features/interface/demande/shared'
  import { useDemandeCAPPStore } from '@/features/interface/demande/capp/useDemandeCAPPStore'
  import { PropertyStatus } from '@/features/domaine/property-status/PropertyStatus'
  import { CAPP_ACTIONS } from '@/features/interface/demande/capp/useDemandeCAPPActions'
  import { inject } from 'vue'
  import { useCodePostalCommune } from '@/features/interface/composables/useCodePostalCommune'
  import type { AdresseDTO } from '@/features/api/extranet/dto/shared'

  const store = useDemandeCAPPStore()
  const { setupFieldWithKey, setupCustomUpdater } = store
  const { meta, readonly, model } = storeToRefs(store)
  const { searchCommune } = inject(CAPP_ACTIONS)!

  const { codePostalModel, communeModel, getCpCommuneLabel } = useCodePostalCommune({
    getCodePostal: () => model?.value?.apprenti?.adresse?.codePostal,
    getCommune: () => model?.value?.apprenti?.adresse?.commune,
    updateValues: setupCustomUpdater((patch, value) => {
      const apprenti = patch.apprenti!
      if (!apprenti.adresse) {
        apprenti.adresse = {} as AdresseDTO
      }
      apprenti.adresse.codePostal = value.code ?? apprenti.adresse.codePostal
      apprenti.adresse.commune = value.commune ?? apprenti.adresse.commune
    }),
  })

  const voieModel = setupFieldWithKey<string>('apprenti.adresse.voie', true)
  const complementAdresseModel = setupFieldWithKey<string>('apprenti.adresse.complement', true)
  const telephoneModel = setupFieldWithKey<string>('apprenti.telephone', true)
  const emailModel = setupFieldWithKey<string>('apprenti.email', true)
</script>
